import { useCallback, useEffect, useLayoutEffect, useRef, useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Loader2, ChevronDown, ChevronRight, Link2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import BlockCalendarRangeNav from './BlockCalendarRangeNav';
import PeriodizationWeekSessionsModal from './PeriodizationWeekSessionsModal';
import { endOfWeekYmd, isCurrentCalendarWeek, weekIndexForYmd } from '../../utils/blockCalendarWeekUtils';
import {
  formatAttributeDisplay,
} from '../../utils/periodizationPeriodBuckets';
import AttributeValueBadge from './AttributeValueBadge';
import BlockAttributeValuePopover from './BlockAttributeValuePopover';
import BlockAttributeSeriesModal from './BlockAttributeSeriesModal';
import BlockCalendarScopeCell from './BlockCalendarScopeCell';
import { buildAttributeWeekScopeMeta, resolveEditableTarget } from '../../utils/blockCalendarAttributeUtils';

function findWeekContext(block, weekStartYmd) {
  for (const phase of block.phases || []) {
    if (weekStartYmd < phase.start_date || weekStartYmd > phase.end_date) continue;
    const micro = phase.microcycles?.find(
      (m) => weekStartYmd >= m.week_start && weekStartYmd <= m.week_end
    );
    return { phase, micro };
  }
  return { phase: null, micro: null };
}

function attrLabel(t, typeKey, fallback) {
  const k = `periodization.attributes.${typeKey}`;
  const tr = t(k);
  return tr !== k ? tr : fallback || typeKey;
}

function findMicroForWeek(block, weekStartYmd) {
  const ctx = findWeekContext(block, weekStartYmd);
  if (!ctx.micro) return null;
  return { micro: ctx.micro, phase: ctx.phase };
}

function pickBlockForWeek(blocks, weekStartYmd, preferredBlockId) {
  if (!blocks?.length) return null;
  const withMicro = blocks
    .map((block) => ({ block, ...findMicroForWeek(block, weekStartYmd) }))
    .filter((row) => row.micro);
  if (!withMicro.length) return null;
  const preferred = withMicro.find((row) => String(row.block.id) === String(preferredBlockId));
  return preferred || withMicro[0];
}

const PHASE_COLORS = {
  base: 'bg-emerald-500/80',
  build: 'bg-primary/80',
  peak: 'bg-rose-500/80',
  taper: 'bg-sky-500/80',
  recovery: 'bg-muted',
  transition: 'bg-violet-500/70',
  custom: 'bg-secondary',
};

const KIND_LABEL = {
  period: 'period',
  fixed_weeks: 'fixedWeeks',
  target_race: 'targetRace',
};

export default function BlockCalendarView({ focusBlockId, linkBlocksToDesign = true }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [rangeIdx, setRangeIdx] = useState(null);
  const [focusedBlockId, setFocusedBlockId] = useState(null);
  const [weekModal, setWeekModal] = useState(null);
  const [expandedBlocks, setExpandedBlocks] = useState(() => new Set());
  const [attrSeriesModal, setAttrSeriesModal] = useState(null);
  const rangeInitialized = useRef(false);
  const scrollContainerRef = useRef(null);
  const currentWeekColRef = useRef(null);
  const stickyBlockColRef = useRef(null);

  const load = useCallback(
    async (opts = {}) => {
      setLoading(true);
      try {
        const params = {};
        if (focusBlockId) params.focus_block_id = focusBlockId;
        if (opts.from) params.from = opts.from;
        if (opts.to) params.to = opts.to;

        const res = await api.get('/api/training/periodization/calendar', { params });
        setView(res.data);

        if (res.data?.bounds_weeks?.length && !rangeInitialized.current) {
          rangeInitialized.current = true;
          setRangeIdx([
            weekIndexForYmd(res.data.from, res.data.bounds_weeks),
            weekIndexForYmd(res.data.to, res.data.bounds_weeks),
          ]);
        }
      } catch (e) {
        console.error(e);
        setView(null);
      } finally {
        setLoading(false);
      }
    },
    [focusBlockId]
  );

  useEffect(() => {
    rangeInitialized.current = false;
    setRangeIdx(null);
    setFocusedBlockId(focusBlockId || null);
    load();
  }, [focusBlockId, load]);

  const handleBlockFocus = useCallback(
    (block) => {
      const bounds = view?.bounds_weeks;
      if (!bounds?.length || !block) return;
      const startIdx = weekIndexForYmd(block.start_date, bounds);
      const endIdx = weekIndexForYmd(block.end_date, bounds);
      const nextIdx = [startIdx, endIdx];
      setFocusedBlockId(block.id);
      setRangeIdx(nextIdx);
      const from = bounds[startIdx];
      const to = endOfWeekYmd(bounds[endIdx]);
      load({ from, to });
    },
    [view?.bounds_weeks, load]
  );

  const handleRangeCommit = useCallback(
    (nextIdx) => {
      setRangeIdx(nextIdx);
      const bounds = view?.bounds_weeks;
      if (!bounds?.length) return;
      const from = bounds[nextIdx[0]];
      const to = endOfWeekYmd(bounds[nextIdx[1]]);
      load({ from, to });
    },
    [view?.bounds_weeks, load]
  );

  const weeks = view?.weeks || [];
  const blocks = view?.blocks || [];
  const boundsWeeks = view?.bounds_weeks || [];
  const preferredBlockId = focusedBlockId || focusBlockId || view?.focus_block_id;

  const openWeekModal = useCallback((block, weekStartYmd) => {
    const found = findMicroForWeek(block, weekStartYmd);
    if (!found) return;
    setWeekModal({
      blockId: block.id,
      blockName: block.name,
      micro: found.micro,
      phase: found.phase,
    });
  }, []);

  const openWeekModalFromHeader = useCallback(
    (weekStartYmd) => {
      const picked = pickBlockForWeek(blocks, weekStartYmd, preferredBlockId);
      if (!picked) return;
      setWeekModal({
        blockId: picked.block.id,
        blockName: picked.block.name,
        micro: picked.micro,
        phase: picked.phase,
      });
    },
    [blocks, preferredBlockId]
  );

  const toggleBlockExpanded = (blockId) => {
    setExpandedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  };

  const handleWeekSessionsUpdated = useCallback(() => {
    if (view?.from && view?.to) {
      load({ from: view.from, to: view.to });
    } else {
      load();
    }
  }, [load, view?.from, view?.to]);

  const scrollToCurrentWeek = useCallback(() => {
    const container = scrollContainerRef.current;
    const weekCol = currentWeekColRef.current;
    const stickyCol = stickyBlockColRef.current;
    if (!container || !weekCol) return;
    if (container.scrollWidth <= container.clientWidth) return;

    const stickyWidth = stickyCol?.getBoundingClientRect().width || 0;
    const containerLeft = container.getBoundingClientRect().left;
    const weekLeft = weekCol.getBoundingClientRect().left;
    container.scrollLeft += weekLeft - containerLeft - stickyWidth;
  }, []);

  const hasCurrentWeekInView = weeks.some(isCurrentCalendarWeek);

  useLayoutEffect(() => {
    if (loading || !hasCurrentWeekInView) return;
    scrollToCurrentWeek();
  }, [loading, weeks, view?.from, view?.to, hasCurrentWeekInView, scrollToCurrentWeek]);

  if (loading && !view) {
    return (
      <div className='flex min-h-[200px] items-center justify-center py-12'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  const weekIndex = (weekStart, blockStart, blockEnd) => {
    const start = new Date(`${weekStart}T12:00:00`);
    const bStart = new Date(`${blockStart}T12:00:00`);
    const bEnd = new Date(`${blockEnd}T12:00:00`);
    if (start > bEnd || start < bStart) return -1;
    return weeks.indexOf(weekStart);
  };

  if (!view?.catalog_blocks?.length && !blocks.length) {
    return <p className='py-4 text-sm text-muted-foreground'>{t('periodization.noBlocks')}</p>;
  }

  return (
    <div className='space-y-3'>
      {boundsWeeks.length > 0 && rangeIdx && (
        <BlockCalendarRangeNav
          boundsWeeks={boundsWeeks}
          catalogBlocks={view.catalog_blocks}
          rangeIdx={rangeIdx}
          onRangeChange={setRangeIdx}
          onRangeCommit={handleRangeCommit}
          focusBlockId={focusedBlockId || focusBlockId || view.focus_block_id}
          onBlockFocus={handleBlockFocus}
        />
      )}

      {loading && (
        <div className='flex items-center gap-2 text-xs text-muted-foreground'>
          <Loader2 className='h-3.5 w-3.5 animate-spin' />
          {t('periodization.calendar.updating')}
        </div>
      )}

      <Card>
        <CardHeader className='px-4 pb-2 pt-4 md:px-6'>
          <CardTitle className='text-sm'>{t('periodization.calendar.stackedView')}</CardTitle>
          <p className='text-xs text-muted-foreground'>{t('periodization.calendar.weekClickHint')}</p>
          <p className='text-[10px] text-muted-foreground'>{t('periodization.calendar.attrClickHint')}</p>
          <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground'>
            <span className='inline-flex items-center gap-1.5'>
              <span className='inline-flex items-center gap-0.5'>
                <Link2 className='h-3 w-3 text-primary/70' aria-hidden />
                <span className='h-0.5 w-6 rounded bg-primary/45' aria-hidden />
                <Link2 className='h-3 w-3 text-primary/70' aria-hidden />
              </span>
              {t('periodization.calendar.scopeBlock')}
            </span>
            <span className='inline-flex items-center gap-1.5'>
              <span className='inline-flex items-center gap-0.5'>
                <Link2 className='h-3 w-3 text-violet-500/75' aria-hidden />
                <span className='h-0.5 w-6 rounded bg-violet-500/45' aria-hidden />
                <Link2 className='h-3 w-3 text-violet-500/75' aria-hidden />
              </span>
              {t('periodization.calendar.scopePhase')}
            </span>
          </div>
        </CardHeader>
        <CardContent ref={scrollContainerRef} className='overflow-x-auto px-4 pb-4 md:px-6'>
          <table className='w-full min-w-[640px] border-collapse text-xs'>
            <thead>
              <tr>
                <th
                  ref={stickyBlockColRef}
                  data-sticky-block-col
                  className='sticky left-0 z-[1] min-w-[180px] border-b border-border bg-card p-2 text-left'
                >
                  {t('periodization.blocks')}
                </th>
                {weeks.map((w) => {
                  const isCurrentWeek = isCurrentCalendarWeek(w);
                  const weekHasSessions = !!pickBlockForWeek(blocks, w, preferredBlockId);
                  return (
                    <th
                      key={w}
                      ref={isCurrentWeek ? currentWeekColRef : undefined}
                      className={cn(
                        'min-w-[48px] border-b border-border p-0.5 font-normal',
                        isCurrentWeek
                          ? 'bg-primary/15 font-semibold text-primary ring-1 ring-inset ring-primary/35'
                          : 'text-muted-foreground'
                      )}
                      aria-current={isCurrentWeek ? 'date' : undefined}
                    >
                      <button
                        type='button'
                        disabled={!weekHasSessions}
                        onClick={() => openWeekModalFromHeader(w)}
                        className={cn(
                          'w-full rounded px-1 py-0.5 tabular-nums',
                          weekHasSessions && 'cursor-pointer hover:bg-accent/60 hover:text-accent-foreground'
                        )}
                        title={weekHasSessions ? t('periodization.calendar.openWeek') : undefined}
                      >
                        {w.slice(5)}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {blocks.map((block) => {
                const depth = block.depth || (block.parent_block_id ? 1 : 0);
                const isFocused =
                  focusBlockId && String(block.id) === String(focusBlockId);
                const isExpanded = expandedBlocks.has(block.id);
                const calendarAttrs = block.calendar_attributes || [];
                const canExpand = calendarAttrs.length > 0;
                return (
                  <Fragment key={block.id}>
                    <tr className={cn(isFocused && 'bg-primary/5')}>
                      <td
                        className='sticky left-0 z-[1] border-b border-border bg-card p-2'
                        style={{ paddingLeft: 8 + depth * 16 }}
                      >
                        <div className='flex items-start gap-1'>
                          {canExpand ? (
                            <button
                              type='button'
                              className='mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              onClick={() => toggleBlockExpanded(block.id)}
                              aria-expanded={isExpanded}
                              title={t('periodization.calendar.toggleParams')}
                            >
                              {isExpanded ? (
                                <ChevronDown className='h-3.5 w-3.5' />
                              ) : (
                                <ChevronRight className='h-3.5 w-3.5' />
                              )}
                            </button>
                          ) : (
                            <span className='inline-block w-4 shrink-0' />
                          )}
                          <div className='min-w-0'>
                            {linkBlocksToDesign ? (
                              <Link
                                to={`/training-plan/design?blockId=${block.id}`}
                                className='font-medium hover:text-primary'
                              >
                                {block.name}
                              </Link>
                            ) : (
                              <span className='font-medium'>{block.name}</span>
                            )}
                            <div className='text-[10px] text-muted-foreground'>
                              {t(`periodization.blockKinds.${KIND_LABEL[block.block_kind] || 'fixedWeeks'}`)}
                              {canExpand ? ` · ${calendarAttrs.length} ${t('periodization.calendar.paramCount')}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      {weeks.map((w) => {
                        const wi = weekIndex(w, block.start_date, block.end_date);
                        const inRange = wi >= 0;
                        const isCurrentWeek = isCurrentCalendarWeek(w);
                        const { phase, micro } = findWeekContext(block, w);
                        const canOpenWeek = !!(inRange && micro);
                        return (
                          <td
                            key={w}
                            className={cn(
                              'border-b border-border p-0.5',
                              isCurrentWeek && 'bg-primary/5'
                            )}
                          >
                            {inRange && phase ? (
                              <button
                                type='button'
                                disabled={!canOpenWeek}
                                onClick={() => openWeekModal(block, w)}
                                className={cn(
                                  'flex h-6 w-full items-center justify-center rounded text-[9px] text-primary-foreground',
                                  PHASE_COLORS[phase.phase_kind] || PHASE_COLORS.custom,
                                  micro?.is_deload && 'ring-1 ring-amber-400',
                                  canOpenWeek && 'cursor-pointer hover:opacity-90'
                                )}
                                title={
                                  micro
                                    ? t('periodization.calendar.openWeekBlock', {
                                        block: block.name,
                                        week: micro.week_number_in_block,
                                        count: micro.linked_session_count || 0,
                                      })
                                    : phase.name
                                }
                              >
                                {micro ? `W${micro.week_number_in_block}` : ''}
                              </button>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>

                    {isExpanded &&
                      calendarAttrs.map((attr) => {
                        const scopeMeta = buildAttributeWeekScopeMeta(block, weeks, attr, {
                          isWeekInRange: (w, b) =>
                            weekIndex(w, b.start_date, b.end_date) >= 0,
                        });
                        return (
                        <tr key={`${block.id}-${attr.type_key}`} className='bg-muted/15'>
                          <td
                            className='sticky left-0 z-[1] border-b border-border bg-card/95 p-2 pl-8 text-[10px] text-muted-foreground'
                            style={{ paddingLeft: 20 + depth * 16 }}
                          >
                            <button
                              type='button'
                              className='text-left font-medium text-foreground hover:text-primary hover:underline'
                              title={t('periodization.calendar.editAttrSeries')}
                              onClick={() => setAttrSeriesModal({ block, attr })}
                            >
                              {attrLabel(t, attr.type_key, attr.name)}
                            </button>
                          </td>
                          {weeks.map((w, weekIdx) => {
                            const wi = weekIndex(w, block.start_date, block.end_date);
                            const inRange = wi >= 0;
                            const isCurrentWeek = isCurrentCalendarWeek(w);
                            const { phase, micro } = findWeekContext(block, w);
                            const scopeCell = scopeMeta[weekIdx] || {};
                            const resolved = inRange
                              ? {
                                  value: scopeCell.value,
                                  bucket: scopeCell.bucket,
                                }
                              : { value: null, bucket: null };
                            const editTarget = inRange
                              ? resolveEditableTarget(attr, block, phase, micro)
                              : { editable: false };
                            const scopeTitle = resolved.bucket
                              ? `${t(`periodization.assessmentManager.buckets.${resolved.bucket}`)} · ${formatAttributeDisplay(resolved.value, attr)}`
                              : inRange && resolved.value != null
                                ? formatAttributeDisplay(resolved.value, attr)
                                : undefined;
                            return (
                              <td
                                key={w}
                                className={cn(
                                  'border-b border-border p-1 text-center text-[9px] tabular-nums text-muted-foreground',
                                  isCurrentWeek && 'bg-primary/5'
                                )}
                              >
                                {inRange ? (
                                  <BlockCalendarScopeCell
                                    spanRole={scopeCell.spanRole}
                                    bucket={resolved.bucket}
                                    title={scopeTitle}
                                  >
                                    <BlockAttributeValuePopover
                                      attr={attr}
                                      value={resolved.value}
                                      editable={editTarget.editable}
                                      targetType={editTarget.targetType}
                                      microId={editTarget.microId}
                                      phaseId={editTarget.phaseId}
                                      blockId={editTarget.blockId}
                                      onSaved={handleWeekSessionsUpdated}
                                    >
                                      <AttributeValueBadge value={resolved.value} type={attr} compact />
                                    </BlockAttributeValuePopover>
                                  </BlockCalendarScopeCell>
                                ) : null}
                              </td>
                            );
                          })}
                        </tr>
                        );
                      })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <PeriodizationWeekSessionsModal
        open={!!weekModal}
        onOpenChange={(next) => {
          if (!next) setWeekModal(null);
        }}
        micro={weekModal?.micro}
        phase={weekModal?.phase}
        blockId={weekModal?.blockId}
        onUpdated={handleWeekSessionsUpdated}
      />

      <BlockAttributeSeriesModal
        open={!!attrSeriesModal}
        onOpenChange={(next) => {
          if (!next) setAttrSeriesModal(null);
        }}
        block={attrSeriesModal?.block}
        attr={attrSeriesModal?.attr}
        weeks={weeks}
        onSaved={handleWeekSessionsUpdated}
      />
    </div>
  );
}
